import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, Calendar, Database, ExternalLink, FileText, Mail, Copy, Check, ArrowLeft } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useCanonical } from "../lib/seoHelpers";
import { safeJsonLd } from "../lib/safeJsonLd";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * /research/appeal-outcomes-2026 — the "Asset #1" linkable research page
 * from the Oct 8 backlink playbook.
 *
 * Pulls aggregate numbers from the live triage-analyzer dataset via the
 * existing /api/triage/aggregate endpoint. Honest about sample size:
 * never publishes percentages below MIN_VOLUME threshold.
 *
 * This is the page journalists cite, so it includes:
 *   - A direct, quotable headline number up top
 *   - Breakdown by platform and category
 *   - Explicit methodology note (sample, dates, limits)
 *   - "Cite this page" block
 *   - Press email contact
 *
 * COMPLIANCE: never publishes "appeal-success rate" or "recovery rate" —
 * our dataset is analyzer submissions (what people search for help with),
 * not outcome data. The page says this plainly.
 */

export default function AppealOutcomesReportPage() {
  useCanonical("https://socialdisputeco.com/research/appeal-outcomes-2026");
  useEffect(() => {
    document.title = "Appeal Outcomes Report 2026 — Live Data from the Free Suspension Analyzer | Social Dispute Co";
    const desc = "Live research report based on real submissions to the Social Dispute Co Free Suspension Analyzer. Platform distribution, category breakdown, and severity data. Updated continuously. Free to cite.";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", desc);
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/triage/aggregate`);
        if (res.ok) setData(await res.json());
      } catch (_e) {
        // Silent — the page still renders without live data.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const total = data?.total || 0;
  const canonicalURL = "https://socialdisputeco.com/research/appeal-outcomes-2026";
  const lastReviewed = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" });

  const citation = `Social Dispute Co. "Appeal Outcomes Report 2026 — Live Analyzer Data." Updated ${lastReviewed}. ${canonicalURL}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      // Silent
    }
  };

  const reportSchema = {
    "@context": "https://schema.org",
    "@type": "Report",
    name: "Appeal Outcomes Report 2026 — Live Analyzer Data",
    datePublished: "2026-10-08",
    dateModified: new Date().toISOString().slice(0, 10),
    author: { "@type": "Organization", name: "Social Dispute Co" },
    publisher: {
      "@type": "Organization",
      name: "Social Dispute Co",
      url: "https://socialdisputeco.com/",
    },
    description:
      "Live research report based on real submissions to the Social Dispute Co Free Suspension Analyzer. Platform distribution, policy category breakdown, and severity data.",
    url: canonicalURL,
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]" data-testid="outcomes-report-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(reportSchema) }}
      />

      <Header />

      <main>
        {/* Hero + headline number */}
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#3f4816]/40 to-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-5 md:px-10">
            <p className="text-xs uppercase tracking-widest text-[#d9fb06] font-mono mb-3 flex items-center gap-2">
              <Database className="w-3.5 h-3.5" />
              Live research · Free to cite · Updated continuously
            </p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#dfddd6] uppercase leading-[0.95] mb-5">
              Appeal Outcomes Report 2026
            </h1>
            <p className="text-lg md:text-xl text-[#dfddd6] max-w-3xl leading-relaxed mb-4">
              <strong className="text-[#d9fb06]">
                {loading
                  ? "Loading live data…"
                  : total > 0
                    ? `${total.toLocaleString()} real suspension cases analyzed.`
                    : "This report is seeded as submissions accumulate."}
              </strong>{" "}
              Platform distribution, policy category breakdown, and severity data from real submissions to our
              Free Suspension Analyzer. No outcome percentages published — those are proprietary to each platform.
            </p>
            <p className="text-xs text-[#888680] font-mono">
              Last reviewed: {lastReviewed} · Methodology at bottom · Free to quote with a link to this page
            </p>
          </div>
        </section>

        {/* Headline findings — only show numbers that have enough volume */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-5 md:px-10 space-y-8">
            {loading ? (
              <div className="rounded-xl border border-[#3f4816]/50 bg-[#252825] p-8 text-center">
                <p className="text-[#888680]">Loading live aggregate data…</p>
              </div>
            ) : total === 0 ? (
              <div className="rounded-xl border border-[#3f4816]/50 bg-[#252825] p-8">
                <h2 className="text-xl font-black text-[#d9fb06] uppercase mb-2">Data accumulating</h2>
                <p className="text-[#dfddd6] mb-4">
                  This research report updates automatically as submissions to the Free Suspension Analyzer accumulate.
                  Platform distribution, category breakdown, and severity data will appear here once there is enough
                  volume to publish honestly.
                </p>
                <p className="text-sm text-[#888680]">
                  Methodology note: we never publish percentages below <strong>50 submissions per bucket</strong>, to
                  avoid misleading figures. Journalists needing an earlier-stage data pull can email{" "}
                  <a href="mailto:press@socialdisputeco.com" className="text-[#d9fb06] hover:underline">
                    press@socialdisputeco.com
                  </a>
                  .
                </p>
              </div>
            ) : (
              <>
                {/* Platform distribution */}
                {data?.byPlatform?.length > 0 && (
                  <ReportSection
                    title="Platform Distribution"
                    subtitle={`Of ${total.toLocaleString()} analyzed cases, the following platforms are most represented:`}
                    items={data.byPlatform.map((p) => ({
                      label: p.key || "Unknown",
                      count: p.count,
                      percent: Math.round((p.count / total) * 100),
                      hidePercent: p.count < 50,
                    }))}
                  />
                )}

                {/* Category distribution */}
                {data?.byCategory?.length > 0 && (
                  <ReportSection
                    title="Policy Category Breakdown"
                    subtitle="How our classifier maps notice language into policy categories (publicly-verifiable platform-documentation taxonomies):"
                    items={data.byCategory.map((c) => ({
                      label: c.key || "Unknown",
                      count: c.count,
                      percent: Math.round((c.count / total) * 100),
                      hidePercent: c.count < 50,
                    }))}
                  />
                )}

                {/* Severity distribution */}
                {data?.bySeverity?.length > 0 && (
                  <ReportSection
                    title="Severity Distribution"
                    subtitle="Severity reflects the complexity of the appeal documentation required, not the probability of success:"
                    items={data.bySeverity.map((s) => ({
                      label: s.key || "Unknown",
                      count: s.count,
                      percent: Math.round((s.count / total) * 100),
                      hidePercent: s.count < 50,
                    }))}
                  />
                )}
              </>
            )}
          </div>
        </section>

        {/* Methodology */}
        <section className="py-12 bg-[#252825] border-y border-[#3f4816]/40">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <h2 className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase mb-4 flex items-center gap-3">
              <FileText className="w-5 h-5" />
              Methodology
            </h2>
            <div className="space-y-3 text-sm text-[#dfddd6] leading-relaxed">
              <p>
                <strong>Data source:</strong> This report pulls directly from the Social Dispute Co Free Suspension
                Analyzer at{" "}
                <a href="/analyze" className="text-[#d9fb06] hover:underline">
                  /analyze
                </a>
                . Users paste their platform suspension notice; our client-side classifier maps the notice language to
                the most likely policy category and severity. With user consent, the (anonymized) classification is
                recorded.
              </p>
              <p>
                <strong>What is in the dataset:</strong> platform name, broad account type (personal / business /
                creator / ad account), classifier-assigned policy category, classifier-assigned severity, and
                timestamp. No PII. No user names, emails, phone numbers, IP addresses, or free-text notice content are
                retained.
              </p>
              <p>
                <strong>Volume threshold:</strong> No percentages are published for any bucket with fewer than 50
                submissions. This avoids misleading precision from small samples.
              </p>
              <p>
                <strong>What this report is NOT:</strong> we do <strong>not</strong> publish appeal-success rates or
                recovery percentages. Platform appeal outcomes are proprietary to each platform&apos;s internal review
                teams and cannot be measured honestly from the outside. Any service claiming a specific &ldquo;recovery
                rate&rdquo; is either fabricating the number or operating without compliance guardrails.
              </p>
              <p>
                <strong>Update cadence:</strong> This page pulls live data on every load. The methodology itself is
                reviewed quarterly.
              </p>
            </div>
          </div>
        </section>

        {/* Cite this page */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <aside className="rounded-2xl border-2 border-[#d9fb06]/40 bg-[#302f2c] p-6">
              <h3 className="text-lg font-black text-[#d9fb06] uppercase mb-3 flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Cite this page
              </h3>
              <pre className="bg-[#1a1c1b] rounded-lg p-4 text-sm text-[#dfddd6] font-mono whitespace-pre-wrap break-all mb-3" data-testid="citation-text">
                {citation}
              </pre>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#d9fb06] text-[#1a1c1b] text-sm font-bold hover:bg-[#d9fb06]/90"
                  data-testid="copy-citation-btn"
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copied" : "Copy citation"}
                </button>
                <a
                  href="mailto:press@socialdisputeco.com?subject=Data%20pull%20request"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#3f4816] text-[#d9fb06] text-sm font-bold hover:bg-[#3f4816]/40"
                >
                  <Mail className="w-3.5 h-3.5" />
                  Request a custom pull
                </a>
              </div>
              <p className="text-xs text-[#888680] mt-4">
                Free to quote with a link to this page. For press interviews, custom data cuts (by platform, violation
                type, or date range), or methodology questions, email{" "}
                <a href="mailto:press@socialdisputeco.com" className="text-[#d9fb06] hover:underline">
                  press@socialdisputeco.com
                </a>
                .
              </p>
            </aside>
          </div>
        </section>

        {/* Related */}
        <section className="py-12">
          <div className="max-w-3xl mx-auto px-5 md:px-10">
            <h2 className="text-sm uppercase tracking-widest text-[#888680] font-mono mb-4">Related research</h2>
            <ul className="space-y-2">
              <li>
                <Link to="/data/suspension-trends" className="text-[#d9fb06] hover:underline inline-flex items-center gap-1">
                  Live Suspension Trends dashboard
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </li>
              <li>
                <Link to="/data/reports" className="text-[#d9fb06] hover:underline inline-flex items-center gap-1">
                  All enforcement reports
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </li>
              <li>
                <Link to="/tools/tos-tracker" className="text-[#d9fb06] hover:underline inline-flex items-center gap-1">
                  Platform ToS change tracker
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </li>
              <li>
                <Link to="/press" className="text-[#d9fb06] hover:underline inline-flex items-center gap-1">
                  Press kit (founder bio, logos, more stats)
                  <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </li>
            </ul>
            <p className="mt-6">
              <Link to="/data/reports" className="text-sm text-[#888680] hover:text-[#d9fb06] inline-flex items-center gap-1">
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to all reports
              </Link>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function ReportSection({ title, subtitle, items }) {
  const slug = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <article className="rounded-2xl border border-[#3f4816]/50 bg-[#252825] p-6 md:p-8" data-testid={`report-section-${slug}`}>
      <h2 className="text-xl md:text-2xl font-black text-[#d9fb06] uppercase mb-2">{title}</h2>
      <p className="text-sm text-[#888680] mb-6">{subtitle}</p>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div
            key={item.label + i}
            className="flex items-center justify-between gap-4 p-3 rounded-lg bg-[#1a1c1b] border border-[#3f4816]/30"
          >
            <p className="text-[#dfddd6] font-medium flex-1 truncate">{item.label}</p>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-xs text-[#888680] font-mono">n = {item.count}</span>
              {!item.hidePercent ? (
                <span className="text-sm text-[#d9fb06] font-bold font-mono w-14 text-right">{item.percent}%</span>
              ) : (
                <span className="text-xs text-[#888680] italic w-14 text-right" title="Volume too low to publish percentage honestly">
                  below n=50
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}
